import React, { useEffect, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import PermissionsGrid from './components/PermissionsGrid';
import AdminTabs from './components/AdminTabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import './admin-panel.css';

// Страница /admin/roles (раздел admin-roles, по умолчанию только System Admin):
// список ролей, создание своих, матрица «раздел × view/edit/delete».
function RolesPermissionsPage() {
    const [roles, setRoles] = useState([]);
    const [pages, setPages] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [matrix, setMatrix] = useState({});   // pageKey -> {view, edit, delete}
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ title: '', description: '' });

    const selected = roles.find(r => r.id === selectedId) || null;

    const fetchRoles = () =>
        api.get('/api/admin/roles')
            .then(r => { setRoles(r.data); return r.data; })
            .catch(err => { setError(err.response?.data?.error || 'Failed to load roles'); return []; });

    useEffect(() => {
        fetchRoles().then(list => { if (list.length) setSelectedId(list[0].id); });
        api.get('/api/admin/pages')
            .then(r => setPages(r.data))
            .catch(err => setError(err.response?.data?.error || 'Failed to load pages'));
    }, []);

    useEffect(() => {
        if (!selectedId) return;
        const role = roles.find(r => r.id === selectedId);
        if (!role || role.full_access) { setMatrix({}); setDirty(false); return; }
        api.get(`/api/admin/roles/${selectedId}/permissions`)
            .then(r => {
                const map = {};
                for (const row of r.data) {
                    map[row.page_key] = { view: !!row.can_view, edit: !!row.can_edit, delete: !!row.can_delete };
                }
                setMatrix(map);
                setDirty(false);
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to load role matrix'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const getCell = (pageKey, action) => ({ checked: !!matrix[pageKey]?.[action], marked: false });

    const toggle = (pageKey, action) => {
        setMatrix(prev => {
            const page = { view: false, edit: false, delete: false, ...prev[pageKey] };
            page[action] = !page[action];
            return { ...prev, [pageKey]: page };
        });
        setDirty(true);
    };

    // Групповое переключение колонки секции (Epic 8 фаза 3): один клик по
    // заголовку выставляет действие всем страницам секции.
    const toggleSection = (pageKeys, action, value) => {
        setMatrix(prev => {
            const next = { ...prev };
            for (const pk of pageKeys) {
                next[pk] = { view: false, edit: false, delete: false, ...next[pk], [action]: value };
            }
            return next;
        });
        setDirty(true);
    };

    const saveMatrix = () => {
        setSaving(true);
        setError(null);
        const permissions = Object.entries(matrix).map(([page_key, p]) => ({ page_key, ...p }));
        api.put(`/api/admin/roles/${selectedId}/permissions`, { permissions })
            .then(() => setDirty(false))
            .catch(err => setError(err.response?.data?.error || 'Failed to save'))
            .finally(() => setSaving(false));
    };

    const createRole = (e) => {
        e.preventDefault();
        setError(null);
        api.post('/api/admin/roles', createForm)
            .then(r => {
                setShowCreate(false);
                setCreateForm({ title: '', description: '' });
                fetchRoles().then(() => setSelectedId(r.data.id));
            })
            .catch(err => setError(err.response?.data?.error || 'Failed to create role'));
    };

    const deleteRole = () => {
        if (!selected || selected.is_builtin) return;
        if (!window.confirm(`Delete role "${selected.title}"?`)) return;
        setError(null);
        api.delete(`/api/admin/roles/${selected.id}`)
            .then(() => fetchRoles().then(list => setSelectedId(list.length ? list[0].id : null)))
            .catch(err => setError(err.response?.data?.error || 'Failed to delete role'));
    };

    return (
        <>
            <HeaderLogged />
            <div className="roles-page">
                <div className="roles-page__head">
                    <h2>Roles &amp; Permissions</h2>
                    <button type="button" className="admin-panel__new-btn" onClick={() => setShowCreate(s => !s)}>
                        <FontAwesomeIcon icon={faPlus} /> New Role
                    </button>
                </div>

                <AdminTabs />

                {error && (
                    <div className="admin-panel__error">
                        <FontAwesomeIcon icon={faCircleExclamation} /> {error}
                    </div>
                )}

                {showCreate && (
                    <form className="admin-panel__inline-form" onSubmit={createRole}>
                        <input placeholder="Role name" required
                            value={createForm.title}
                            onChange={e => setCreateForm({ ...createForm, title: e.target.value })} />
                        <input placeholder="Description"
                            value={createForm.description}
                            onChange={e => setCreateForm({ ...createForm, description: e.target.value })} />
                        <button type="submit" className="admin-panel__save-btn">Create</button>
                    </form>
                )}

                <div className="roles-page__tabs">
                    {roles.map(r => (
                        <button key={r.id} type="button"
                            className={`roles-page__tab${r.id === selectedId ? ' is-active' : ''}`}
                            onClick={() => setSelectedId(r.id)}>
                            {r.title}
                            <span className="admin-panel__muted"> · {r.users}</span>
                        </button>
                    ))}
                </div>

                {selected && (
                    <>
                        <div className="roles-page__bar">
                            <span>
                                <strong>{selected.title}</strong>
                                {selected.description ? <span className="admin-panel__muted"> — {selected.description}</span> : null}
                            </span>
                            <span className="roles-page__bar-btns">
                                {!selected.is_builtin && (
                                    <button type="button" className="roles-page__delete-btn" onClick={deleteRole} title="Delete role">
                                        <FontAwesomeIcon icon={faTrash} /> Delete
                                    </button>
                                )}
                                {!selected.full_access && (
                                    <button type="button" className="admin-panel__save-btn"
                                        disabled={!dirty || saving} onClick={saveMatrix}>
                                        {saving ? 'Saving…' : 'Save Changes'}
                                    </button>
                                )}
                            </span>
                        </div>

                        {selected.full_access ? (
                            <div className="roles-page__full-access">
                                This role has <strong>full access</strong> to every section, including ones added
                                in the future. There is no matrix to edit.
                            </div>
                        ) : (
                            <div className="roles-page__grid-wrap">
                                <PermissionsGrid pages={pages} getCell={getCell} onToggle={toggle} onToggleSection={toggleSection} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

export default RolesPermissionsPage;
